import * as React from 'react'

import ReactTable, { Filter } from 'react-table'
import 'react-table/react-table.css'
import { DownTime, AlarmGroup, DowntimePropertyName } from '../../model/DownTime'
import { DowntimeDateRange } from '../../model/DowntimeDateRange'
import { DownTimeAction, FailedAction } from '../../model/DownTimeEnums'
import { RevertInfo } from '../../model/RevertInfo'
import './Downtimes.scss'
import { Selector } from './Selector'
import ReactDOM from 'react-dom'
import SiteConfig from '../../model/SiteConfig'
import { getGeneratorName } from '../../model/SiteConfig'
import { InfoSplitModal } from './InfoSplitModal'
import { CopyModal } from './CopyModal'
import { RevertWarningModal } from './RevertWarningModal'
import { timeProps } from './ReadOnlyEventList'
import ScadaObject from '../../model/ScadaObject'
import DownTimeTimeOnInput from './TableCells/DownTimeTimeOnInput'
import DownTimeTimeOffInput from './TableCells/DownTimeTimeOffInput'
import NewEventDropDown from './NewEventDropDown'
import { DowntimeEditStatus } from '../../model/DowntimeEditStatus'
import { Prompt } from 'react-router'
import { ValidationResults, ValidationMessage } from '../../model/ValidationResults'
import ValidationMessageList from './ValidationMessageList'
import { TextEntry } from './TableCells/TextEntry'
import { GroupCell } from './TableCells/GroupCell'
import { PenalisingStatusCell } from './TableCells/PenalisingStatusCell'
import { ScadaObjectCell } from './TableCells/ScadaObjectCell'
import { SubGroupCell } from './TableCells/SubGroupCell'
import { DownTimeActionsButton } from './DownTimeActionsButton'
import { DurationFormatter, DateFormatter } from '../../common/Formatters'
import { AddNewDownTimeButton } from './AddNewDownTimeButton'
import { Message, ErrorMessage, InfoMessage, WarnMessage } from '../../model/Message'
import { mvcLinkMessage, downtimeWelcomeMessage } from '../../rest-api/WelcomeMessages'
import { LockStatus } from '../../model/DownTimeEnums'
import { validateTimeOnTimeOff } from './Validation'
import { Dropdown, Modal, Button } from 'react-bootstrap'
import AvailabilityDetails from '../../model/AvailabilityDetails'
import { AvailabilityTable } from './AvailabilityTable'
import { configuration } from '../../rest-api/Configuration'

interface IDispatchProps {
  getDowntimeEditStatus: (
    marketKey: string,
    apikey: string,
    month: Date
  ) => Promise<DowntimeEditStatus>
  getSiteAvailability: (
    marketKey: string,
    apikey: string,
    month: Date
  ) => Promise<AvailabilityDetails>
  getGeneratorAvailability: (
    marketKey: string,
    generatorApikey: string,
    month: Date
  ) => Promise<AvailabilityDetails>
  updateDowntimeEditStatus: (
    marketKey: string,
    apikey: string,
    month: Date,
    status: string
  ) => Promise<void>
  getDowntimes: (
    marketKey: string,
    apikey: string,
    from: Date,
    to: Date,
    scadaObjectKey: string,
    scadaObjectType: string
  ) => Promise<DownTime[]>
  updateDowntime: (marketKey: string, downtime: DownTime) => Promise<ValidationResults | undefined>
  createDowntime: (marketKey: string, downtime: DownTime) => Promise<ValidationResults | undefined>
  checkDowntimeValidation: (
    marketKey: string,
    downtime: DownTime
  ) => Promise<ValidationResults | undefined>
  revertDowntime: (marketKey: string, downtime: DownTime) => Promise<DownTime>
  splitDowntime: (marketKey: string, downtime: DownTime, splitTime: Date) => Promise<DownTime[]>
  copyDowntime: (marketKey: string, source: DownTime, targets: DownTime[]) => Promise<void>
  canEditTimeOff: (marketKey: string, scadaObjectKey: string, timeOn: Date) => Promise<boolean>
  getPenalisingStatus: (marketKey: string) => Promise<string[]>
  getAlarmGroups: (marketKey: string) => Promise<AlarmGroup[]>
  sendMessage: (message: Message) => void
  recalculate: (marketKey: string, apikey: string, month: Date) => Promise<string>
}

interface IStateProps {
  site: SiteConfig
}

type State = {
  loading: boolean
  showAvailability: boolean
  siteAvailability?: AvailabilityDetails
  siteAvailabilityMessage: string
  generatorAvailability?: AvailabilityDetails
  generatorAvailabilityMessage: string
  recalculationInProgress: boolean
  fetchError: string
  scadaObjectKey: string
  scadaObjectType: string
  scadaObjects: ScadaObject[]
  localDowntimes: DownTime[]
  downtimeEditStatus: string
  downtimeEditStatusMessage: string
  revertInfo?: RevertInfo
  penalisingStatus: string[]
  alarmGroups: AlarmGroup[]
  downtimeToSplit?: DownTime
  downtimeToCopy?: DownTime
  downtimesLoaded: boolean
  dateRange: DowntimeDateRange
}

const MIN_YEAR: number = 1753

type Props = IDispatchProps & IStateProps

var getLastMonthDateRange = (): DowntimeDateRange => {
  let now = new Date();
  let start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  let lastDayOfLastMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();

  return {
    year: start.getFullYear(),
    month: start.getMonth(),
    daysFrom: start.getDate(),
    daysTo: lastDayOfLastMonth
  };
}

// remove leading zeroes from numbers, so that T1 matches T01
var ignoreLeadingZeros = (baseString: string): string => {
  var updatedString = '';
  var previousWasNumber = false;
  for (var i = 0; i < baseString.length; i++) {
    if(baseString[i] >= '0' && baseString[i] <= '9') {
      if(previousWasNumber || baseString[i] > '0') {
        updatedString += baseString[i];
        previousWasNumber = true;
      }
    } else {
      updatedString += baseString[i];
      previousWasNumber = false;
    }
  }
  return updatedString;
}

// search the turbine name for the filter string, but also
// search for the filter string in the turbine name with 
// leading zeros removed, so 't1' will find 'kel_t01'
export var filterScadaObject = (filter: Filter, row: any): boolean =>
  row._original.scadaObjectName.toLocaleLowerCase()
    .indexOf(filter.value.toLocaleLowerCase()) >= 0 ||
  ignoreLeadingZeros(row._original.scadaObjectName.toLocaleLowerCase())
    .indexOf(filter.value.toLocaleLowerCase()) >= 0

/**
 * Works with native javascript dates.
 * Remember that months are Zero-Indexed. So when reading values out to the user we increment by 1.
 */
export class Downtimes extends React.Component<Props, State> {
  dataTableElement?: ReactTable<DownTime>
  constructor(props: Props) {
    super(props)

    this.state = {
      loading: false,
      showAvailability: false,
      siteAvailabilityMessage: 'Press search to load availability',
      generatorAvailabilityMessage: 'Press search to load availability',
      recalculationInProgress: false,
      fetchError: '',
      scadaObjectKey: '',
      scadaObjectType: '',
      scadaObjects: [],
      downtimeEditStatus: '',
      downtimeEditStatusMessage: 'Press search to load status',
      penalisingStatus: [],
      alarmGroups: [],
      downtimeToSplit: undefined,
      downtimeToCopy: undefined,
      localDowntimes: [],
      revertInfo: undefined,
      downtimesLoaded: false,
      dateRange: getLastMonthDateRange(),
    }
  }

  loadDropDownData = (): void => {
    this.setState({ penalisingStatus: [], alarmGroups: [] }, () => {
      this.props
        .getPenalisingStatus(this.props.site.marketKey)
        .then(result => this.setState({ penalisingStatus: result }))
        .catch(err => {
          this.props.sendMessage(ErrorMessage('Failed to get penalising statuses', err))
          console.info('Failed to get penalising statuses')
        })

      this.props
        .getAlarmGroups(this.props.site.marketKey)
        .then(result => this.setState({ alarmGroups: result }))
        .catch(err => {
          this.props.sendMessage(ErrorMessage('Failed to get alarm groups', err))
          console.info('Failed to get alarm groups')
        })
    })
  }

  filterAlarmGroupsByTechnology = (alarmGroups: AlarmGroup[]): AlarmGroup[] =>
    // using includes because downtime api uses friendly name but SMART API does not
    alarmGroups.filter(
      g => g.technology.includes(this.props.site.technology) || g.technology === 'All'
    )

  replaceNewDowntime = (newDownTime: DownTime): void => {
    this.setState({
      localDowntimes: this.state.localDowntimes.map((downTime: DownTime) =>
        downTime.id === 'newEvent' ? newDownTime : downTime
      )
    })
  }

  onNewEventCancel = (): void => {
    let downtimes = this.state.localDowntimes.filter(d => d.isNew === false)
    this.setState({
      localDowntimes: downtimes
    })
  }

  openInfoSplitModal = (downtime: DownTime) => {
    this.setState({
      downtimeToSplit: downtime
    })
  }

  openCopyModal = (downtime: DownTime) => {
    this.setState({
      downtimeToCopy: downtime
    })
  }

  keepNewVisible = () => {
    let newDownTimes = document.getElementsByClassName('new-down-time')
    if (newDownTimes.length > 0) {
      let newDownTime = newDownTimes.item(0)
      if (newDownTime !== null && newDownTime !== undefined) {
        newDownTime.scrollIntoView()
      }
    }
  }

  addNewDownTime = (): void => {
    var downTime: DownTime = new DownTime(undefined, this.props.site.key)
    downTime.scadaObjectKey = this.state.scadaObjects[0].apiKey
    downTime.scadaObjectName = this.state.scadaObjects[0].name
    downTime.timeOn = this.getFromDate()
    downTime.timeOff = new Date(new Date(this.getFromDate()).setHours(1))
    downTime.group = this.state.alarmGroups[0].name
    downTime.subGroup = this.state.alarmGroups[0].subGroups[0]
    downTime.penalisingStatus = this.state.penalisingStatus[0]
    this.setState({ localDowntimes: [downTime].concat(this.state.localDowntimes) }, () => {
      this.keepNewVisible()
      // make sure the validation is updated on creation
      this.updateNewDowntime(downTime)
    })
  }

  onSaveNewEvent = (downtime: DownTime): void => {
    this.replaceDowntime({
      ...downtime,
      lockStatus: LockStatus.savingNew
    })

    if (!downtime.IsValid) {
      return
    } else {
      var newDownTime = new DownTime({
        ...downtime,
        isNew: false
      })

      this.props
        .createDowntime(this.props.site.marketKey, newDownTime)
        .then((validation?: ValidationResults) => {
          if (validation) {
            this.replaceDowntime(
              new DownTime({
                ...downtime,
                lockStatus: LockStatus.notLocked,
                validation: validation,
                failedAction: undefined
              })
            )
          } else {
            this.replaceNewDowntime(
              new DownTime({
                ...newDownTime,
                lockStatus: LockStatus.notLocked,
                failedAction: undefined
              })
            )
          }
        })
        .catch((err: string) => {
          this.props.sendMessage(ErrorMessage('Failed to replace new downtime', err))
          this.replaceDowntime(
            new DownTime({
              ...downtime,
              failedAction: new FailedAction(err, DownTimeAction.save),
              lockStatus: LockStatus.notLocked
            })
          )
        })
    }
  }

  checkCanEditTimeOff = (downtime: DownTime): void => {
    this.replaceDowntime(new DownTime({ ...downtime, lockStatus: LockStatus.checking }))
    this.props
      .canEditTimeOff(this.props.site.marketKey, downtime.scadaObjectKey, downtime.timeOn)
      .then(result => {
        let newDowntime = new DownTime({
          ...downtime,
          canEditTimeOff: result,
          lockStatus: LockStatus.notLocked
        })
        if (!result) {
          newDowntime.SetValidationMessagesForProperties(
            [
              {
                property: DowntimePropertyName.TimeOff,
                message: 'The time off of this downtime event cannot be edited.'
              }
            ],
            [DowntimePropertyName.TimeOff]
          )
        }
        this.replaceDowntime(newDowntime)
      })
      .catch((err: string) => {
        this.props.sendMessage(ErrorMessage('Failed to check canEditTimeOff', err))
        this.replaceDowntime(new DownTime({ ...downtime, lockStatus: LockStatus.notLocked }))
      })
  }

  createUtcDate = (y: number, m: number, d: number) => new Date(`${y}-${m + 1}-${d} UTC`)

  getFromDate = (): Date => {
    var fromDate = this.createUtcDate(this.state.dateRange.year, this.state.dateRange.month, this.state.dateRange.daysFrom)
    return fromDate
  }

  getToDate = (): Date => {
    var toDate = this.createUtcDate(this.state.dateRange.year, this.state.dateRange.month, this.state.dateRange.daysTo)
    toDate.setDate(toDate.getDate() + 1)
    return toDate
  }

  getDowntimesWithCurrentParameters = (
    dateRange: DowntimeDateRange,
    scadaObjectKey: string,
    scadaObjectType: string,
    scadaObjects: ScadaObject[]
  ) => {

    // clear the current downtimes then fetch
    this.setState(
      {
        localDowntimes: [],
        downtimeEditStatus: '',
        downtimeEditStatusMessage: 'Loading',
        loading: true,
        scadaObjectKey: scadaObjectKey,
        scadaObjectType: scadaObjectType,
        fetchError: '',
        scadaObjects: scadaObjects,
        dateRange,
      },
      () => {
        this.props
          .getDowntimes(
            this.props.site.marketKey,
            this.props.site.key,
            this.getFromDate(),
            this.getToDate(),
            scadaObjectKey,
            scadaObjectType
          )
          .then(result =>
            this.setState({ localDowntimes: result, loading: false, downtimesLoaded: true })
          )
          .catch(error => this.setState({ fetchError: error, loading: false }))
        this.props
          .getDowntimeEditStatus(
            this.props.site.marketKey,
            this.props.site.key,
            this.createUtcDate(this.state.dateRange.year, this.state.dateRange.month, 1)
          )
          .then(result =>
            this.setState({ downtimeEditStatus: result.status, downtimeEditStatusMessage: '' })
          )
          .catch(error => {
            this.props.sendMessage(ErrorMessage('Failed to load downtime edit status', error))
            this.setState({ downtimeEditStatusMessage: 'Failed to load ' + error })
          })
      }
    )
  }

  clearAvailabilities = () => {
    this.setState({
      siteAvailability: undefined,
      siteAvailabilityMessage: 'Loading site availability details',
      generatorAvailability: undefined,
      generatorAvailabilityMessage:
        this.state.scadaObjectKey && this.state.scadaObjectKey !== 'All'
          ? 'Loading ' + getGeneratorName(this.props.site.technology) + ' availability details'
          : 'No ' + getGeneratorName(this.props.site.technology) + ' selected',
    })
  }

  getAvailabilities = () => {
    this.props
      .getSiteAvailability(
        this.props.site.marketKey,
        this.props.site.key,
        new Date(Date.UTC(this.state.dateRange.year, this.state.dateRange.month, 1))
      )
      .then(result => {
        this.setState({ siteAvailability: result, siteAvailabilityMessage: '' })
      })
      .catch(error => {
        this.props.sendMessage(ErrorMessage('Failed to load site availability', error))
        this.setState({ siteAvailabilityMessage: 'Failed to load ' + error })
      })
    if (this.state.scadaObjectKey && this.state.scadaObjectKey !== 'All') {
      this.props
        .getGeneratorAvailability(
          this.props.site.marketKey,
          this.state.scadaObjectKey,
          new Date(Date.UTC(this.state.dateRange.year, this.state.dateRange.month, 1))
        )
        .then(result =>
          this.setState({ generatorAvailability: result, generatorAvailabilityMessage: '' })
        )
        .catch(error => {
          this.props.sendMessage(
            ErrorMessage(
              'Failed to load ' +
                getGeneratorName(this.props.site.technology) +
                ' availability',
              error
            )
          )
          this.setState({ generatorAvailabilityMessage: 'Failed to load ' + error })
        })
    }
  }

  showAvailability = () => {
    this.clearAvailabilities()
    this.getAvailabilities()
    this.setState({ showAvailability: true })
  }

  hideAvailability = () => this.setState({ showAvailability: false })

  scadaAnalysisCommandLine = () =>
    `/r ${this.props.site.marketKey} /s ${
      this.props.site.key
    } /y ${this.state.dateRange.year} /m ${this.state.dateRange.month + 1} ${
      this.state.scadaObjectKey && this.state.scadaObjectKey !== 'All'
        ? `/o ${this.state.scadaObjectKey}`
        : ''
    }`

  startRecalculation = () => {
    if(this.state.dateRange.year >= MIN_YEAR) {
      this.setState({ recalculationInProgress: true }, () =>
        this.props
          .recalculate(
            this.props.site.marketKey,
            this.props.site.key,
            new Date(Date.UTC(this.state.dateRange.year, this.state.dateRange.month, 1))
          )
          .then(trackingURL =>
            this.setState({ recalculationInProgress: false }, () =>
              this.props.sendMessage(
                InfoMessage(
                  'Recalculation started',
                  <span>
                    {"You can track the progress of the recalculation "}
                    <a target="_blank" rel="noopener noreferrer" href={trackingURL}>
                      here
                    </a>
                    .
                  </span>
                )
              )
            )
          )
          .catch(reason =>
            this.setState({ recalculationInProgress: false }, () =>
              this.props.sendMessage(
                ErrorMessage('Recalculation failed to start', `Could not start the recalculation: ${reason}`)
              )
            )
          )
      )
    } else {
      this.props.sendMessage(
        WarnMessage('Unable to recalculate', `Cannot recalculate, ${this.state.dateRange.year} is out of range (before ${MIN_YEAR})`)
      )
    }
  }

  filterAllEvents = () =>
    this.setState({
      localDowntimes: this.state.localDowntimes.map(
        (downtime: DownTime) => new DownTime({ ...downtime, doNotFilter: false })
      )
    })

  // see https://github.com/tannerlinsley/react-table/issues/192#issuecomment-442270734
  addFilterPlaceholder = () => {
    document.querySelectorAll<HTMLInputElement>('div.rt-th > input').forEach(filter => {
      filter.placeholder = 'Filter..'
      filter.title = 'Only show events that contain the specified text in this column'
      filter.onblur = e => this.filterAllEvents()
    })
    document.querySelectorAll<HTMLInputElement>('div.rt-th.duration > input').forEach(filter => {
      filter.placeholder = 'Seconds..'
      filter.title = 'Hide events with duration less than the specified number of seconds'
      filter.onblur = e => this.filterAllEvents()
    })
  }

  componentDidMount(): void {
    this.props.sendMessage(downtimeWelcomeMessage)
    this.props.sendMessage(mvcLinkMessage)
    this.addFilterPlaceholder()
    if (this.props.site) {
      this.loadDropDownData()
    }
  }

  // cache downtimes for local editing
  componentDidUpdate(previousProps: Readonly<Props>, previousState: Readonly<State>): void {
    if (this.props.site) {
      if (!!!previousProps.site || previousProps.site.marketKey !== this.props.site.marketKey) {
        this.loadDropDownData()
      }
      if (!!previousProps.site && previousProps.site.key !== this.props.site.key) {
        this.setState({
          localDowntimes: [],
          downtimeEditStatus: '',
          downtimeEditStatusMessage: 'Press search to load status'
        })
      }
      if (this.unsavedChanges()) {
        window.onbeforeunload = () => true
      } else {
        window.onbeforeunload = null
      }
    }

    if (this.dataTableElement) {
      let theads = (ReactDOM.findDOMNode(this.dataTableElement) as Element).getElementsByClassName(
        'rt-thead'
      )
      let tbody = (ReactDOM.findDOMNode(this.dataTableElement) as Element).getElementsByClassName(
        'rt-tbody'
      )[0]

      if (tbody.scrollHeight > tbody.clientHeight) {
        Array.from(theads).map(t => t.classList.add('vertical-scrollbar-present'))
      } else {
        Array.from(theads).map(t => t.classList.remove('vertical-scrollbar-present'))
      }
    }
  }

  scrollToDowntime = (downtime: DownTime) => {
    if (downtime.id) {
      const element = document.getElementById(downtime.id)
      if (element) {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      }
    }
  }

  splitDowntimeEvent = (downtime: DownTime, splitTime: Date) => {
    this.replaceDowntime(new DownTime({ ...downtime, lockStatus: LockStatus.splitting }))
    this.setState({
      downtimeToSplit: undefined
    })
    this.props
      .splitDowntime(this.props.site.marketKey, downtime, splitTime)
      .then(result => {
        this.setState({
          localDowntimes: this.state.localDowntimes
            .filter(existing => existing.id !== downtime.id)
            .concat(result.map(downtime => new DownTime({ ...downtime, doNotFilter: true })))
        })
        this.scrollToDowntime(downtime)
      })
      .catch(err => {
        this.props.sendMessage(ErrorMessage('Split failed', err))
        this.replaceDowntime(
          new DownTime({
            ...downtime,
            failedAction: new FailedAction(err, DownTimeAction.split)
          })
        )
      })
  }

  closeInfoSplitModal = () => {
    this.setState({
      downtimeToSplit: undefined
    })
  }

  checkResetDowntime = (downtime: DownTime) => {
    let eventsToRevert = this.state.localDowntimes.filter(
      (e: DownTime) =>
        e.scadaObjectKey === downtime.scadaObjectKey &&
        e.parentEventTimeOn &&
        downtime.parentEventTimeOn &&
        e.parentEventTimeOn.valueOf() - downtime.parentEventTimeOn.valueOf() === 0
    )
    if (eventsToRevert.length === 1) {
      this.resetDowntime(downtime, eventsToRevert)
    } else {
      this.setState({ revertInfo: new RevertInfo(downtime, eventsToRevert) })
    }
  }

  updateNewDowntime = (newDowntime: DownTime): void => {
    let validationMessages: ValidationMessage[] = validateTimeOnTimeOff(
      newDowntime,
      this.state.localDowntimes
    )
    newDowntime.SetValidationMessagesForProperties(validationMessages, [
      DowntimePropertyName.TimeOn,
      DowntimePropertyName.TimeOff
    ])
    this.replaceDowntime(newDowntime)
    this.props
      .checkDowntimeValidation(this.props.site.marketKey, newDowntime)
      .then((validation?: ValidationResults) =>
        this.replaceDowntime(new DownTime({ ...newDowntime, validation: validation }))
      )
  }

  setInvalidDateValidationMessage = (newDowntime: DownTime, property: string): void => {
    newDowntime.SetValidationMessagesForProperties(
      [
        {
          property: property,
          message: 'Please enter a valid ISO DateTime string of format YYYY-MM-DD hh:mm:ss.'
        }
      ],
      [property]
    )
    this.replaceDowntime(newDowntime)
  }

  replaceDowntime = (newDownTime: DownTime): void => {
    this.setState({
      localDowntimes: this.state.localDowntimes.map((downTime: DownTime) =>
        downTime.id === newDownTime.id ? newDownTime : downTime
      )
    })
  }

  resetDowntime = (original: DownTime, eventsToRevert: DownTime[]) => {
    this.setState({
      localDowntimes: this.state.localDowntimes.map(existing =>
        eventsToRevert.findIndex(toRevert => existing.id === toRevert.id) === -1
          ? existing
          : new DownTime({ ...existing, lockStatus: LockStatus.reverting })
      ),
      revertInfo: undefined
    })
    this.props
      .revertDowntime(this.props.site.marketKey, eventsToRevert[0])
      .then(result => {
        this.setState({
          localDowntimes: [
            result,
            ...this.state.localDowntimes.filter(
              existing =>
                !(
                  existing.scadaObjectKey === result.scadaObjectKey &&
                  eventsToRevert.some(
                    toRevert => existing.timeOn.getTime() === toRevert.timeOn.getTime()
                  )
                )
            )
          ]
        })
      })
      .catch(err => {
        this.props.sendMessage(ErrorMessage('Revert failed', err))
        this.replaceDowntime(
          new DownTime({
            ...original,
            failedAction: new FailedAction(err, DownTimeAction.revert)
          })
        )
        this.setState({
          localDowntimes: this.state.localDowntimes.map(existing =>
            eventsToRevert.findIndex(toRevert => existing.id === toRevert.id) === -1
              ? existing
              : new DownTime({ ...existing, lockStatus: LockStatus.notLocked })
          ),
          revertInfo: undefined
        })
      })
  }

  shouldAddEventBeDisabled = (): boolean => {
    if (this.state.localDowntimes && this.state.downtimesLoaded) {
      return this.state.localDowntimes.find(d => d.id === 'newEvent') !== undefined
    } else {
      return true
    }
  }

  validateAndSaveDowntime = (downtime: DownTime): void => {
    let validationMessages: ValidationMessage[] = validateTimeOnTimeOff(
      downtime,
      this.state.localDowntimes
    )
    downtime.SetValidationMessagesForProperties(validationMessages, [
      DowntimePropertyName.TimeOn,
      DowntimePropertyName.TimeOff
    ])
    this.saveDowntime(downtime)
  }

  saveDowntime = (downtime: DownTime) => {
    // don't save edits for new events they must be explicitly saved from the context menu
    // also don't allow multiple saves to be in flight at one time because this can cause
    // exceptions in the api the changes will be saved when the current save completes
    if (downtime.saveInFlight || downtime.isNew) {
      // still need to update the local copy of this downtime
      this.replaceDowntime(downtime)
      return
    }
    this.replaceDowntime(
      new DownTime({
        ...downtime,
        saveInFlight: true,
        doNotFilter: true
      })
    )
    this.props
      .updateDowntime(this.props.site.marketKey, downtime)
      .then((validation?: ValidationResults) => {
        // need to consider the current outstanding edits, not the value at the time the query was sent
        // this avoids losing edits made while earlier edits were being saved
        const currentDowntimeEvent = this.state.localDowntimes.find(
          original => original.id === downtime.id
        )
        if (currentDowntimeEvent) {
          const validatedDowntime = new DownTime({
            ...currentDowntimeEvent,
            parentEventTimeOn: downtime.parentEventTimeOn || downtime.timeOn,
            saveInFlight: false,
            validation,
            failedAction: undefined
          })
          this.replaceDowntime(validatedDowntime)
          if (validatedDowntime.HasBeenEdited(downtime)) {
            this.saveDowntime(validatedDowntime)
          }
          this.scrollToDowntime(validatedDowntime)
        }
      })
      .catch((err: string) => {
        this.props.sendMessage(ErrorMessage('Failed to update downtime', err))
        // need to consider the current outstanding edits, not the value at the time the query was sent
        // this avoids losing edits made while earlier edits were being saved
        const currentDowntimeEvent = this.state.localDowntimes.find(
          original => original.id === downtime.id
        )
        if (currentDowntimeEvent) {
          const validatedDowntime = new DownTime({
            ...currentDowntimeEvent,
            parentEventTimeOn: downtime.parentEventTimeOn || downtime.timeOn,
            saveInFlight: false,
            failedAction: new FailedAction(err, DownTimeAction.save)
          })
          this.replaceDowntime(validatedDowntime)
          if (validatedDowntime.HasBeenEdited(downtime)) {
            this.saveDowntime(validatedDowntime)
          }
          this.scrollToDowntime(validatedDowntime)
        }
      })
  }

  unsavedChanges = () => {
    return (
      this.state.localDowntimes &&
      this.state.localDowntimes.some(d => d.isNew || d.saveInFlight || d.failedAction !== undefined)
    )
  }

  public render() {
    const {
      scadaObjectKey,
      scadaObjectType,
      localDowntimes,
      alarmGroups,
      penalisingStatus,
      scadaObjects
    } = this.state

    const { site } = this.props

    const renderClickableActions = ({ original }: { original: DownTime }) =>
      original.lockStatus !== LockStatus.notLocked ? (
        <p>{original.lockStatus}...</p>
      ) : original.saveInFlight ? (
        <p>Saving...</p>
      ) : original.isNew ? (
        <NewEventDropDown
          editableManualDowntime={original}
          onSaveClick={this.onSaveNewEvent}
          onCancelClick={this.onNewEventCancel}
        ></NewEventDropDown>
      ) : (
        <DownTimeActionsButton
          downTime={original}
          onInfoSplit={this.openInfoSplitModal}
          onCopy={this.openCopyModal}
          onRevert={this.checkResetDowntime}
          onSave={this.saveDowntime}
          checkCanEditTimeOff={this.checkCanEditTimeOff}
        ></DownTimeActionsButton>
      )

    const closeCopyModal = () => {
      this.setState({
        downtimeToCopy: undefined
      })
    }

    const copyToSingleEvent = (source: DownTime, target: DownTime): DownTime =>
      new DownTime({
        ...target,
        penalisingStatus: source.penalisingStatus,
        group: source.group,
        subGroup: source.subGroup,
        comment: source.comment,
        saveInFlight: true,
        doNotFilter: true
      })

    const clearSaving = (downtime: DownTime) =>
      new DownTime({
        ...downtime,
        saveInFlight: false,
        parentEventTimeOn: downtime.parentEventTimeOn || downtime.timeOn,
        failedAction: undefined
      })

    const copyDowntimeSettings = (source: DownTime, targets: DownTime[]) => {
      this.setState(
        {
          localDowntimes: this.state.localDowntimes.map(event =>
            targets.some(e => e.id === event.id) ? copyToSingleEvent(source, event) : event
          ),
          downtimeToCopy: undefined
        },
        () =>
          this.props
            .copyDowntime(site.marketKey, source, targets)
            .then(() =>
              this.setState({
                localDowntimes: this.state.localDowntimes.map(event =>
                  targets.some(e => e.id === event.id) ? clearSaving(event) : event
                )
              })
            )
            .catch((error: string) => {
              this.props.sendMessage(ErrorMessage('Failed to copy downtime settings', error))
              this.replaceDowntime(
                new DownTime({
                  ...source,
                  failedAction: new FailedAction(error, DownTimeAction.copy)
                })
              )
              this.setState({
                localDowntimes: this.state.localDowntimes.map(event =>
                  targets.some(e => e.id === event.id) ? clearSaving(event) : event
                )
              })
            })
      )
    }

    return (
      <div className="parent grow">
        <Prompt
          when={this.unsavedChanges()}
          message="You have unsaved changes, are you sure you want to leave?"
        />
        <div className="downtimeStatusRow">
          <Selector
            marketKey={site && site.marketKey}
            siteKey={site && site.key}
            search={this.getDowntimesWithCurrentParameters}
            scadaObjectKey={scadaObjectKey}
            scadaObjectType={scadaObjectType}
            reportError={(message, detail) => this.props.sendMessage(ErrorMessage(message, detail))}
            disabled={this.unsavedChanges()}
            disabledReason={this.unsavedChanges() ? 'There are unsaved changes' : undefined}
            dateRange={this.state.dateRange}
          />
          <section className="filters">
            <Dropdown id="availability-options">
              <Dropdown.Toggle id="availability-options-toggle" disabled={!localDowntimes || !site}>
                {
                    this.state.recalculationInProgress ? 
                       <span> 
                         <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"/>
                         Starting recalculation...
                       </span> : "Availability" 
                }
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-right">
                <Dropdown.Item key="view" onClick={this.showAvailability}>
                  View
                </Dropdown.Item>
                <Dropdown.Item
                  key="recalculate"
                  onClick={this.startRecalculation}
                  title={(!this.state.downtimesLoaded ? 'Cannot recalculate until downtimes are loaded' : 
                         this.state.recalculationInProgress ? 'Recalculation is already in progress' : 
                         undefined)}
                  disabled={this.state.recalculationInProgress || !this.state.downtimesLoaded}
                  style={{pointerEvents: "all"}}
                >
                  Recalculate
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            {!localDowntimes || !site ? (
              <img
                className="greyed-out"
                src="/scadaAnalysisLogo.png"
                alt="Press Search to Enable Launching Scada Analysis"
                title="Press Search to Enable Launching Scada Analysis"
              ></img>
            ) : (
              <a
                download={configuration.ScadaAnalysisLauncher}
                href={
                  'data:application/local; charset=utf-8,' +
                  encodeURIComponent(this.scadaAnalysisCommandLine())
                }
              >
                <img
                  src="/scadaAnalysisLogo.png"
                  alt="Launch Scada Analysis"
                  title="Launch Scada Analysis"
                ></img>
              </a>
            )}
          </section>
        </div>
        <div className="downtimeStatusRow">
          <p className="downtimeStatusTitle">Downtimes Edit Status:</p>
          <TextEntry
            className={'downTimeEditStatus'}
            readOnly={false}
            text={this.state.downtimeEditStatus}
            onChange={newStatus =>
              this.setState(
                { downtimeEditStatus: newStatus, downtimeEditStatusMessage: 'Saving' },
                () =>
                  this.props
                    .updateDowntimeEditStatus(
                      site.marketKey,
                      site.key,
                      new Date(Date.UTC(
                        this.state.dateRange.year,
                        this.state.dateRange.month,
                        1
                      )),
                      newStatus
                    )
                    .then(() => this.setState({ downtimeEditStatusMessage: '' }))
                    .catch(error => {
                      this.props.sendMessage(ErrorMessage('Failed to save', '' + error))
                      this.setState({ downtimeEditStatusMessage: 'failed to save' + error })
                    })
              )
            }
          />
          <p className="downtimeStatusTitle">{this.state.downtimeEditStatusMessage}</p>
        </div>
        <ReactTable
          onSortedChange={() => this.keepNewVisible()}
          showPagination={localDowntimes && localDowntimes.length  > 200}
          defaultPageSize={200}
          minRows={0}
          pageSizeOptions={[5, 10, 50, 100, 200]}
          data={localDowntimes || []}
          loading={this.state.loading}
          noDataText={
            localDowntimes
              ? 'No events found for the selected period'
              : 'Press Search to load some events'
          }
          showPageJump={false}  // page jump in disabled because it has a bug that doesn't correctly display the current page
          ref={element => {
            if (element) {
              // from https://github.com/tannerlinsley/react-table/issues/495#issuecomment-331946312
              this.dataTableElement = element
              let theads = (ReactDOM.findDOMNode(element) as Element).getElementsByClassName(
                'rt-thead'
              )
              let tbody = (ReactDOM.findDOMNode(element) as Element).getElementsByClassName(
                'rt-tbody'
              )[0]

              tbody.addEventListener('scroll', () => {
                for (let i = 0; i < theads.length; i++) {
                  theads[i].scrollLeft = tbody.scrollLeft
                }
              })
            }
          }}
          className={'fixed-height-data-table grow parent'}
          getTheadTrProps={state => ({ style: { minWidth: state.rowMinWidth } })}
          getTrProps={(state: any, rowInfo: any) => ({
            style: { minWidth: state.rowMinWidth },
            className:
              'wordwrap' +
              (rowInfo
                ? (rowInfo.original.Edited() ||
                  rowInfo.original.lockStatus !== LockStatus.notLocked ||
                  rowInfo.original.outstandingEdits
                    ? ' modified'
                    : '') + (rowInfo.original.isNew ? ' new-down-time' : '')
                : ''),
            id: rowInfo ? rowInfo.original.id : undefined
          })}
          columns={[
            {
              accessor: 'scadaObjectKey',
              Header:
                this.state.scadaObjectType.length === 0 ? 'Object Key' : this.state.scadaObjectType,
              Cell: row => (
                <ScadaObjectCell
                  downTime={row.original}
                  replaceDowntime={this.updateNewDowntime}
                  scadaObjects={scadaObjects}
                />
              ),
              filterMethod: (filter: Filter, row: any) =>
                row._original.doNotFilter ||
                row._original.isNew ||
                filterScadaObject(filter, row)
            },
            {
              accessor: 'comment',
              Header: 'Comment',
              Cell: (row: { original: DownTime }) => (
                <TextEntry
                  className={'wordwrap comment'}
                  readOnly={row.original.lockStatus !== LockStatus.notLocked}
                  text={row.original.comment || ''}
                  onChange={comment =>
                    this.saveDowntime(new DownTime({ ...row.original, comment }))
                  }
                />
              )
            },
            {
              accessor: 'description',
              Header: 'Description',
              Cell: row => {
                return <span className="wordwrap">{row.value}</span>
              }
            },
            {
              accessor: 'alarmCode',
              Header: 'Alarm Code',
              Cell: row => (
                <div>
                  <span className="wordwrap">{row.value}</span>{' '}
                  <ValidationMessageList
                    property={DowntimePropertyName.AlarmCode}
                    downtime={row.original}
                  />
                </div>
              )
            },
            {
              accessor: 'timeOn',
              Header: 'Time On',
              getProps: timeProps,
              getHeaderProps: timeProps,
              filterable: false,
              Cell: row =>
                row.original.isNew && row.original.lockStatus === LockStatus.notLocked ? (
                  <DownTimeTimeOnInput
                    newEvent={row.original}
                    downtimes={this.state.localDowntimes}
                    replaceDowntime={this.updateNewDowntime}
                    setInvalidDateValidationMessage={this.setInvalidDateValidationMessage}
                  />
                ) : (
                  DateFormatter(row.value, this.getFromDate())
                ),
              minWidth: 100,
              maxWidth: 200
            },
            {
              accessor: 'timeOff',
              Header: 'Time Off',
              filterable: false,
              getProps: timeProps,
              getHeaderProps: timeProps,
              Cell: row => {
                if (row.original.isNew && row.original.lockStatus === LockStatus.notLocked) {
                  return (
                    <DownTimeTimeOffInput
                      newEvent={row.original}
                      downtimes={this.state.localDowntimes}
                      replaceDowntime={this.updateNewDowntime}
                      setInvalidDateValidationMessage={this.setInvalidDateValidationMessage}
                    />
                  )
                }
                if (
                  row.original.canEditTimeOff &&
                  row.original.lockStatus === LockStatus.notLocked
                ) {
                  return (
                    <DownTimeTimeOffInput
                      newEvent={row.original}
                      downtimes={this.state.localDowntimes}
                      replaceDowntime={this.validateAndSaveDowntime}
                      setInvalidDateValidationMessage={this.setInvalidDateValidationMessage}
                    />
                  )
                } else {
                  return (
                    <div>
                      {DateFormatter(row.value, this.getFromDate())}
                      <ValidationMessageList
                        property={DowntimePropertyName.TimeOff}
                        downtime={row.original}
                      />
                    </div>
                  )
                }
              },
              minWidth: 100,
              maxWidth: 200
            },
            {
              accessor: row => row.DurationInMiliSeconds(this.getToDate(), this.getFromDate()),
              id: 'durationColumn',
              Header: <span title="Duration in selected period (HH:mm:ss)">Duration</span>,
              Cell: row => <span className="wordwrap">{DurationFormatter(row.value)}</span>,
              minWidth: 90,
              maxWidth: 90,
              filterMethod: (filter: Filter, row: any) =>
                row &&
                row._original &&
                (row._original.doNotFilter ||
                  row._original.isNew ||
                  !+filter.value ||
                  row._original.DurationInMiliSeconds(this.getToDate(), this.getFromDate()) > 1000 * +filter.value),
              headerClassName: 'duration'
            },
            {
              accessor: 'group',
              Header: 'Group',
              Cell: row => (
                <GroupCell
                  downtime={row.original}
                  alarmGroups={this.filterAlarmGroupsByTechnology(alarmGroups)}
                  updateDowntime={this.saveDowntime}
                />
              ),
              minWidth: 160,
              maxWidth: 160
            },
            {
              accessor: 'subGroup',
              Header: 'Sub Group',
              Cell: row => (
                <SubGroupCell
                  downtime={row.original}
                  alarmGroups={this.filterAlarmGroupsByTechnology(alarmGroups)}
                  updateDowntime={this.saveDowntime}
                />
              ),
              minWidth: 225,
              maxWidth: 225
            },
            {
              accessor: 'penalisingStatus',
              Header: 'Penalising Status',
              Cell: row => (
                <PenalisingStatusCell
                  downtime={row.original}
                  penalisingStatus={penalisingStatus}
                  updateDowntime={this.saveDowntime}
                />
              ),
              minWidth: 140,
              maxWidth: 140
            },
            {
              Filter: () => (
                <AddNewDownTimeButton
                  disabled={this.shouldAddEventBeDisabled()}
                  onClick={this.addNewDownTime}
                  downTimesLoaded={this.state.downtimesLoaded}
                />
              ),
              Header: 'Action',
              filterable: true,
              minWidth: 90,
              maxWidth: 90,
              Cell: renderClickableActions
            }
          ]}
          defaultFilterMethod={(filter: Filter, row: any): boolean =>
            row._original.doNotFilter ||
            row._original.isNew ||
            String(row[filter.id])
              .toLocaleLowerCase()
              .indexOf(filter.value.toLocaleLowerCase()) >= 0
          }
          filterable
        />
        {this.state.revertInfo && (
          <RevertWarningModal
            ok={() =>
              this.state.revertInfo &&
              this.resetDowntime(
                this.state.revertInfo.downTimeToBeReverted,
                this.state.revertInfo.downTimesToRevert
              )
            }
            cancel={() => this.setState({ revertInfo: undefined })}
            dateFormatter={(date: Date) => DateFormatter(date, this.getFromDate())}
            eventsToWarnAboutReverting={this.state.revertInfo!.downTimesToRevert}
          />
        )}
        {this.state.downtimeToSplit && (
          <InfoSplitModal
            split={this.splitDowntimeEvent}
            close={this.closeInfoSplitModal}
            dateFormatter={DateFormatter}
            downtime={this.state.downtimeToSplit}
            siteTechnology={site.technology}
            marketKey={site.marketKey}
            fromDate={this.getFromDate()}
          />
        )}
        {this.state.downtimeToCopy && (
          <CopyModal
            close={closeCopyModal}
            copy={copyDowntimeSettings}
            getDowntimes={(from: Date, to: Date) =>
              this.props.getDowntimes(site.marketKey, site.key, from, to, 'All', '')
            }
            dateFormatter={(date?: Date) => DateFormatter(date, this.getFromDate())}
            downtime={this.state.downtimeToCopy}
            scadaObjectType={this.state.scadaObjectType}
          />
        )}
        <Modal
          show={this.state.showAvailability}
          onHide={this.hideAvailability}
          centered
          dialogClassName="availability-modal"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Availability: {this.prettyAvailabilityMonth()}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AvailabilityTable
              siteDetails={this.state.siteAvailability}
              siteMessage={this.state.siteAvailabilityMessage}
              generatorDetails={this.state.generatorAvailability}
              generatorMessage={this.state.generatorAvailabilityMessage}
              generatorName={getGeneratorName(this.props.site.technology)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.hideAvailability}>OK</Button>
          </Modal.Footer>
        </Modal>
      </div>
    )
  }

  prettyAvailabilityMonth = (): string => {
    const d = new Date(this.state.dateRange.year, this.state.dateRange.month, 1)
    return d.toLocaleDateString('en-us', { month: 'long', year: 'numeric' })
  }
}
